import { isRouteErrorResponse } from "react-router";

import { buildLocalizedPath } from "~/config/route-config";

/**
 * T828 — the marker a route's `clientAction` returns when its POST's `fetch` failed
 * (`{ ok: false, code: "network" }`), and the one predicate every `shouldRevalidate` on that
 * route's chain asks. A failed fetch does NOT prove the POST never reached the server: the
 * request may have been applied and only its response lost — which is why the screen says
 * «No sabemos si el cambio se ha guardado» and re-reads before saying anything else.
 *
 * What it does prove is that the network is unreliable right now. After such an action the
 * router's default is to re-read EVERY matched loader — the root's included — and with the
 * network down the root's read fails and the whole page falls to the generic error boundary
 * before the route can say what happened (Astra's browser QA, 2026-09-30: real network loss
 * → «Algo se rompió por aquí», 500). So the chain is not re-read here; the route re-reads its
 * own loader, alone, to learn the real state.
 */
export function isNetworkFailureResult(actionResult: unknown): boolean {
  return (
    actionResult != null &&
    typeof actionResult === "object" &&
    (actionResult as { ok?: unknown }).ok === false &&
    (actionResult as { code?: unknown }).code === "network"
  );
}

const TRANSIENT_4XX = new Set([408, 429]);

/**
 * T895 (F1) — a public form's `clientAction`: the server's answer when it arrives, the form's
 * own failure when the submit throws instead (a lost connection, an undecodable 5xx page).
 * Without it the throw reached the route's error boundary and «No pudimos cargar esta página»
 * replaced the form and what she had typed. A thrown `Response` (a redirect) or a deliberate 4xx keeps its path.
 * The `code: "network"` mark lets `shouldRevalidate` skip the re-read that would fail too.
 */
export async function submitOrFormFailure<T, F extends object>(
  serverAction: () => Promise<T>,
  failure: F,
): Promise<T | (F & { code: "network" })> {
  try {
    return await serverAction();
  } catch (e) {
    // A redirect, or a 4xx the action throws on purpose, keeps its own path; a 5xx, a timeout
    // (408) or an edge rate limit (429) is the failure: from her side, retry later.
    if (e instanceof Response || (isRouteErrorResponse(e) && e.status < 500 && !TRANSIENT_4XX.has(e.status))) throw e;
    return { ...failure, code: "network" };
  }
}

/**
 * T972 (preview QA) — the Back Office's `clientAction`: the server's answer, or the network
 * marker alone when the submit's `fetch` fails. No `error` text on purpose: the submitting
 * `ActionForm` says it (once), and screens that print `actionData.error` stay quiet.
 */
export async function adminFormClientAction<T>({ serverAction }: { serverAction: () => Promise<T> }) {
  return submitOrFormFailure(serverAction, { ok: false } as const);
}

/**
 * T972 (preview QA) — a GET a component asks for through a fetcher (the calendar's month): a
 * failed `fetch` comes back as the network marker instead of throwing, so the route's error
 * boundary does not replace the whole screen; the caller sees "no data" and offers its retry.
 * A thrown `Response` (redirect, 4xx/5xx) keeps its own path.
 */
export async function loadOrNetworkFailure<T>(serverLoader: () => Promise<T>): Promise<T | { ok: false; code: "network" }> {
  try {
    return await serverLoader();
  } catch (e) {
    if (e instanceof TypeError) return { ok: false, code: "network" };
    throw e;
  }
}

/**
 * T972 (preview QA) — a route on the chain of a form that can fail on the network: after a
 * failed submit, do not re-read (the read would fail too and take the screen with it).
 */
export function skipRevalidateAfterNetworkFailure(args: { actionResult?: unknown; defaultShouldRevalidate: boolean }): boolean {
  return isNetworkFailureResult(args.actionResult) ? false : args.defaultShouldRevalidate;
}

const ASSESSMENT_PATHS = new Set([buildLocalizedPath("assessment", "es"), buildLocalizedPath("assessment", "en")]);

/**
 * T828 (Astra's browser QA; round-2 audit) — the root's `shouldRevalidate`, in one place.
 *
 * The root re-reads on every navigation (its `sessionUser` chrome follows the cookies), with
 * two narrow exceptions, both about `/evaluacion`:
 * - right after an action whose fetch failed (see `isNetworkFailureResult`);
 * - a GET navigation INTO `/evaluacion` in the same language. That route has a `clientLoader`,
 *   so under single fetch it is already fetched on its own request and the root on a second
 *   one; with the network down the root's request failed and the generic error boundary (a
 *   «500») replaced the page before the route could say «No hemos podido cargar tu fase».
 *   Skipping the root there costs nothing and saves a request: the route's own server
 *   loader still proves the session, and the chrome keeps what it already shows. A change of
 *   language re-reads the root (its locale lives there).
 *
 * A root `clientLoader` with a cached fallback was tried and withdrawn: it sends the root on
 * its own request on EVERY navigation, doubling Function requests (T190 budget).
 */
export function rootShouldRevalidate(args: {
  actionResult?: unknown;
  formMethod?: string;
  currentUrl: URL;
  nextUrl: URL;
}): boolean {
  if (isNetworkFailureResult(args.actionResult)) return false;
  const lang = (u: URL) => u.pathname.split("/")[1]?.toLowerCase();
  // The router matches case-insensitively and tolerates a trailing slash; so does this.
  const path = (u: URL) => u.pathname.toLowerCase().replace(/\/+$/, "");
  const intoAssessment =
    !args.formMethod &&
    ASSESSMENT_PATHS.has(path(args.nextUrl)) &&
    lang(args.currentUrl) === lang(args.nextUrl);
  return !intoAssessment;
}
